import React, { ReactNode, useState, useCallback } from 'react';
import { VisibilityContext } from './contexts';
import {
  updateArtworkVisibility,
  fetchVisibilitySettings as fetchVisibilitySettingsApi
} from '../utils/visibilityApi';

const VisibilityProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [visibilitySettings, setVisibilitySettings] = useState<Array<{ artworkId: string; visibility: 'public' | 'private' }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateVisibility = useCallback(async (artworkId: string, visibility: 'public' | 'private') => {
    try {
      setLoading(true);
      setError(null);

      // Use managed API client (automatic token handling)
      await updateArtworkVisibility(artworkId, visibility);

      setVisibilitySettings(prev => {
        const existing = prev.find(s => s.artworkId === artworkId);
        if (existing) {
          return prev.map(s => s.artworkId === artworkId ? { ...s, visibility } : s);
        } else {
          return [...prev, { artworkId, visibility }];
        }
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update visibility';
      setError(errorMessage);
      throw err; // Re-throw for caller to handle
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshVisibilitySettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Use managed API client (automatic token handling)
      const data = await fetchVisibilitySettingsApi();
      setVisibilitySettings(data.visibilitySettings || []);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch visibility settings';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const getArtworkVisibility = useCallback((artworkId: string): 'public' | 'private' => {
    const setting = visibilitySettings.find(s => s.artworkId === artworkId);
    return setting?.visibility || 'private';
  }, [visibilitySettings]);

  const checkAndUpdateVisibilityForPublicAttribute = useCallback(async (artworkId: string, hasPublicAttribute: boolean) => {
    const currentVisibility = getArtworkVisibility(artworkId);
    
    // If any attribute is made public and the artwork is currently private, make it public
    if (hasPublicAttribute && currentVisibility === 'private') {
      await updateVisibility(artworkId, 'public');
    }
  }, [getArtworkVisibility, updateVisibility]);

  return (
    <VisibilityContext.Provider value={{ 
      visibilitySettings, 
      loading, 
      error, 
      updateVisibility, 
      refreshVisibilitySettings, 
      getArtworkVisibility,
      checkAndUpdateVisibilityForPublicAttribute
    }}>
      {children}
    </VisibilityContext.Provider>
  );
};

export { VisibilityProvider };

